import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/state/AppContext";
import { api } from "@/lib/api";
import { useFavorites } from "@/lib/favorites";
import { localized } from "@/theme/tenantTheme";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BusinessCard } from "@/components/BusinessCard";
import { WorkerCard } from "@/components/WorkerCard";
import { EventCard } from "@/components/EventCard";
import { JobCard } from "@/components/JobCard";
import { OfferBadge } from "@/components/OfferBadge";
import { FavoriteButton } from "@/components/FavoriteButton";
import { advertisedBusinessPath } from "@/lib/whiteLabel";

const TYPES = ["business", "worker", "landmark", "offer", "event", "job"];

function EmptyNote({ t }) {
  return (
    <p
      className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground"
      data-testid="favourites-empty"
    >
      {t("favourites.empty")}
    </p>
  );
}

function CardGrid({ items, render, t }) {
  if (items.length === 0) return <EmptyNote t={t} />;
  return <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{items.map(render)}</div>;
}

export default function Favourites() {
  const { t, lang, auth } = useApp();
  const navigate = useNavigate();
  const favorites = useFavorites();
  const [businessSlugs, setBusinessSlugs] = useState({});

  const entries = favorites.data ?? [];
  const pick = (type) => entries.filter((e) => e.target_type === type).map((e) => e.item);
  const grouped = Object.fromEntries(TYPES.map((type) => [type, pick(type)]));
  const offers = grouped.offer;

  useEffect(() => {
    const missing = [...new Set(offers.map((o) => o.business_id))].filter((id) => !businessSlugs[id]);
    if (missing.length === 0) return;
    Promise.all(
      missing.map((id) =>
        api
          .get(`/directory/businesses/${id}`)
          .then((r) => [id, r.data.slug])
          .catch(() => [id, null]),
      ),
    ).then((pairs) => {
      setBusinessSlugs((prev) => {
        const next = { ...prev };
        pairs.forEach(([id, slug]) => {
          if (slug) next[id] = slug;
        });
        return next;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers.map((o) => o.business_id).join(",")]);

  if (!auth) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-20 text-center" data-testid="favourites-signin-prompt">
        <h1 className="font-heading text-2xl font-semibold">{t("favourites.title")}</h1>
        <p className="mt-3 text-sm text-muted-foreground">{t("favourites.signInNeeded")}</p>
        <Button
          className="mt-6 rounded-full"
          data-testid="favourites-sign-in-btn"
          onClick={() => navigate("/login")}
        >
          {t("common.signIn")}
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8" data-testid="favourites-page">
      <h1 className="font-heading text-4xl font-bold tracking-tight">{t("favourites.title")}</h1>

      {favorites.isLoading ? (
        <p className="mt-8 text-center text-muted-foreground">{t("common.loading")}</p>
      ) : (
        <Tabs defaultValue="business" className="mt-8">
          <TabsList data-testid="favourites-tabs">
            {TYPES.map((type) => (
              <TabsTrigger key={type} value={type} data-testid={`favourites-tab-${type}`}>
                {t(`favourites.tabs.${type}`)} ({grouped[type].length})
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="business" className="mt-6" data-testid="favourites-business-list">
            <CardGrid items={grouped.business} render={(b) => <BusinessCard key={b.id} business={b} />} t={t} />
          </TabsContent>

          <TabsContent value="worker" className="mt-6" data-testid="favourites-worker-list">
            <CardGrid items={grouped.worker} render={(w) => <WorkerCard key={w.id} worker={w} />} t={t} />
          </TabsContent>

          <TabsContent value="event" className="mt-6" data-testid="favourites-event-list">
            <CardGrid items={grouped.event} render={(e) => <EventCard key={e.id} event={e} />} t={t} />
          </TabsContent>

          <TabsContent value="job" className="mt-6" data-testid="favourites-job-list">
            <CardGrid items={grouped.job} render={(j) => <JobCard key={j.id} job={j} />} t={t} />
          </TabsContent>

          <TabsContent value="landmark" className="mt-6 space-y-4" data-testid="favourites-landmark-list">
            {grouped.landmark.length === 0 ? (
              <EmptyNote t={t} />
            ) : (
              grouped.landmark.map((place) => (
                <Link
                  key={place.id}
                  to={`/place/${place.slug}`}
                  data-testid={`favourites-landmark-${place.id}`}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md"
                >
                  <div>
                    <h3 className="font-heading text-lg font-semibold">{localized(lang, place, "name")}</h3>
                    {localized(lang, place, "summary") && (
                      <p className="mt-1 line-clamp-1 text-sm text-muted-foreground">
                        {localized(lang, place, "summary")}
                      </p>
                    )}
                  </div>
                  <FavoriteButton targetType="landmark" targetId={place.id} />
                </Link>
              ))
            )}
          </TabsContent>

          <TabsContent value="offer" className="mt-6 space-y-4" data-testid="favourites-offer-list">
            {grouped.offer.length === 0 ? (
              <EmptyNote t={t} />
            ) : (
              grouped.offer.map((offer) => {
                const slug = businessSlugs[offer.business_id];
                const body = (
                  <>
                    <div className="flex items-center justify-between gap-3">
                      <OfferBadge offer={offer} />
                      <FavoriteButton targetType="offer" targetId={offer.id} />
                    </div>
                    <h3 className="mt-2 font-heading text-lg font-semibold">{localized(lang, offer, "title")}</h3>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                      {localized(lang, offer, "description")}
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground">{offer.business_name}</p>
                  </>
                );
                return slug ? (
                  <Link
                    key={offer.id}
                    to={advertisedBusinessPath({ slug })}
                    data-testid={`favourites-offer-${offer.id}`}
                    className="block rounded-2xl border border-border bg-card p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md"
                  >
                    {body}
                  </Link>
                ) : (
                  <div
                    key={offer.id}
                    data-testid={`favourites-offer-${offer.id}`}
                    className="rounded-2xl border border-border bg-card p-5"
                  >
                    {body}
                  </div>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
